<script setup>
import { ref, computed, onMounted } from 'vue';
import apolloClient from '@/services/apollo';

import AppApiInfoCodeBlock from './ApiInfoCodeBlock.vue';

const graphEndpoint = import.meta.env.VITE_GRAPHQL_ENDPOINT;
const documentationUrl = import.meta.env.VITE_DOCUMENTATION_LINK;

const props = defineProps({
  code: {
    type: Object,
    required: true,
  },
  projectId: {
    type: String,
  },
  variables: {
    type: Object,
    required: false,
  },
});

const computedVariables = computed(() => {
  return props.variables ? { ...props.variables } : {};
});

const isQuery = computed(() => {
  if (!props.code) return true;
  const firstLine = props.code.loc.source.body.trim().split('\n')[0];
  return firstLine.trim().startsWith('query');
});

const variableString = computed(() => {
  const propVariable = props.variables;
  if (!propVariable) {
    return propVariable;
  }
  const varString = JSON.stringify(
    propVariable,
    (k, v) => (v === undefined ? '__undefined' : v),
    '\t'
  ).replace(/"__undefined"/g, 'undefined');
  return varString;
});

const headerJson = computed(() => {
  return JSON.stringify(
    {
      headers: {
        Authorization: `Bearer YOUR_API_KEY`,
        'Content-Type': 'application/json',
      },
    },
    null,
    2
  );
});

function extractOperationName(gqlCode) {
  const innerMatch = gqlCode.match(/{\s*(?:\w+\s*:\s*)?(\w+(?:_\w+)*)\s*\(/);
  if (innerMatch) {
    return innerMatch[1];
  }

  const outerMatch = gqlCode.match(/^(query|mutation)\s+(\w+)/m);
  if (outerMatch) {
    return outerMatch[2];
  }

  return null;
}

const codeLink = computed(() => {
  if (!props.code) return null;
  const operationName = extractOperationName(props.code.loc.source.body);
  const operationType = isQuery.value ? 'query' : 'mutation';
  return operationName
    ? `${documentationUrl}/#${operationType}-${operationName.toLowerCase()}`
    : null;
});

const requiredParams = ref([]);

function extractRequiredParams(gqlCode) {
  const pattern = /\$(\w+):\s*(\[?\w+\!?]?!)\s*/g;
  const matches = [...gqlCode.matchAll(pattern)];
  return matches.map((match) => match[1]);
}

const areRequiredParamsNonEmpty = computed(() => {
  return requiredParams.value.every((param) => {
    const value = computedVariables.value[param];
    if (Array.isArray(value)) {
      return (
        value.length > 0 &&
        value.every((item) => {
          return (
            item !== '' &&
            item !== null &&
            item !== undefined &&
            typeof item === 'object' &&
            Object.values(item).every(
              (fieldValue) =>
                fieldValue !== '' &&
                fieldValue !== null &&
                fieldValue !== undefined
            )
          );
        })
      );
    }

    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true)
    );
  });
});

const isRunButtonDisabled = computed(() => !areRequiredParamsNonEmpty.value);

const responseData = ref(null);
const loading = ref(false);

async function runQuery() {
  try {
    loading.value = true;
    const result = await apolloClient.query({
      query: props.code,
      variables: computedVariables.value,
      fetchPolicy: 'network-only',
    });
    responseData.value = result.data;
  } catch (error) {
    responseData.value = { error: error.message };
  } finally {
    loading.value = false;
  }
}

async function runMutation() {
  try {
    loading.value = true;
    const result = await apolloClient.mutate({
      mutation: props.code,
      variables: computedVariables.value,
    });
    responseData.value = result.data;
  } catch (error) {
    responseData.value = { error: error.message };
  } finally {
    loading.value = false;
  }
}

async function onRun() {
  responseData.value = null;
  if (isQuery.value) {
    await runQuery();
  } else {
    await runMutation();
  }
}

// onMounted(() => {
//   requiredParams.value = extractRequiredParams(props.code.loc.source.body);
// });

const formattedCode = computed(() => {
  if (!props.code) return '';
  return props.code.loc.source.body;
});

const queryTitle = computed(() => {
  const queryName = props.code?.definitions[0]?.name?.value?.trim();
  if (!queryName) return '';
  const title = queryName.replace(/_/g, ' ');
  return title;
});
</script>

<template>
  <div>
    <app-api-info-code-block header="Graph endpoint:" :code="graphEndpoint" />
    <app-api-info-code-block header="Header:" :code="headerJson" />
    <app-api-info-code-block
      v-if="variableString"
      header="Variables:"
      :subheader="
        queryTitle === 'RunJob'
          ? 'Example payload variables. Note: `undefined` is not a valid value for payload variables but can appear here to show all accepted properties.'
          : ''
      "
      :code="variableString"
    />
    <app-api-info-code-block
      :header="isQuery ? 'Query:' : 'Mutation:'"
      :code="formattedCode"
    />
    <app-api-info-code-block
      v-if="responseData"
      header="Response:"
      :code="JSON.stringify(responseData, null, 2)"
    />
    <div class="flex w-full items-center justify-between gap-2">
      <Button
        as="a"
        :href="codeLink"
        target="_blank"
        rel="noopener"
        label="Documentation"
        icon="icon-[ph--file-code] text-xl"
        outlined
        fluid
        size="large"
      >
      </Button>
      <Button
        :label="isQuery ? 'Run query' : 'Run mutation'"
        @click="onRun"
        class="my-3"
        :disabled="isRunButtonDisabled"
        :loading="loading"
        fluid
        size="large"
      />
    </div>
  </div>
</template>
